"use client";

import * as React from "react";
import { Info } from "lucide-react";

export type CheckoutChip = {
  kind: "shape" | "discount" | "surcharge" | "info";
  label: string;
  tone: "green" | "neutral" | "warn";
  isActive: boolean;
};

export type CheckoutConstraint = {
  summary: string;
  severity: "info" | "blocker";
};

export type CheckoutTicketSummary = {
  inlineChips: CheckoutChip[];
  constraints: CheckoutConstraint[];
  policy: { name: string; summary: string; href?: string } | null;
};

const TONE_STYLES: Record<CheckoutChip["tone"], string> = {
  green: "bg-emerald-100 text-emerald-700",
  neutral: "bg-slate-100 text-slate-700",
  warn: "bg-rose-100 text-rose-700",
};

type TicketConditionsChipsProps = {
  chips: CheckoutChip[];
  hasInfo?: boolean;
  onInfoClick?: () => void;
  className?: string;
};

export function TicketConditionsChips({
  chips,
  hasInfo = false,
  onInfoClick,
  className,
}: TicketConditionsChipsProps) {
  if (chips.length === 0 && !hasInfo) return null;

  return (
    <div className={`flex flex-wrap items-center gap-1 ${className ?? ""}`}>
      {chips.map((chip, i) => (
        <span
          key={i}
          className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ${TONE_STYLES[chip.tone]} ${chip.isActive ? "" : "opacity-60"}`}
        >
          {chip.label}
        </span>
      ))}
      {hasInfo && (
        <button
          type="button"
          aria-label="More about this ticket"
          onClick={onInfoClick}
          className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Info className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
